import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { ResolveCompanyModal } from './UnmappedCompanies';

const HUBSPOT_PORTAL_ID = '23367316';
const QUOTES_PER_PAGE = 20;

// React StrictMode mounts effects twice in development. Share only an in-flight
// request so that never results in a duplicate audit call; later visits still
// load a fresh server-cached audit.
const auditRequests = new Map();

const getAudit = (apiFetch, environment) => {
  const requestKey = environment || 'default';
  if (auditRequests.has(requestKey)) return auditRequests.get(requestKey);

  const request = apiFetch('/quote-association-gaps')
    .then(async (response) => {
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || `Audit request failed (${response.status})`);
      }
      return result;
    })
    .finally(() => {
      if (auditRequests.get(requestKey) === request) auditRequests.delete(requestKey);
    });

  auditRequests.set(requestKey, request);
  return request;
};

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const hasAssociation = (singleId, ids) => {
  if (hasValue(singleId)) return true;
  return Array.isArray(ids) && ids.some(hasValue);
};

const isAssociationMissing = (state, singleId, ids) => {
  const normalised = String(state || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (normalised.includes('missing') || ['none', 'unassociated', 'not_associated'].includes(normalised)) {
    return true;
  }
  if (normalised.includes('associated') || ['linked', 'present', 'ok'].includes(normalised)) {
    return false;
  }
  // The plural fields are the quote's actual HubSpot associations; the singular
  // fields can be the mapped target that a historical quote still needs linked to.
  if (Array.isArray(ids)) return !ids.some(hasValue);
  return !hasAssociation(singleId, ids);
};

const isMapped = (state) => {
  const normalised = String(state || '').toLowerCase().replace(/[\s-]+/g, '_');
  return normalised === 'mapped' || normalised.startsWith('mapped_') ||
    ['linked', 'resolved', 'existing'].includes(normalised);
};

const canRepairMappedAssociation = (state) => [
  'mapped',
  'mapping_conflict',
  'mapping_ambiguous',
].includes(String(state || '').toLowerCase().replace(/[\s-]+/g, '_'));

const isAssociationMismatch = (state) => {
  const normalised = String(state || '').toLowerCase().replace(/[\s-]+/g, '_');
  return normalised.includes('mismatch') || normalised.includes('incorrect') ||
    normalised.includes('wrong_company');
};

const isAssociationUnverified = (state) =>
  String(state || '').toLowerCase().replace(/[\s-]+/g, '_').includes('unverified');

const isAssociationUnknown = (state) =>
  String(state || '').toLowerCase().replace(/[\s-]+/g, '_').includes('unknown');

const normaliseState = (state) => String(state || '').toLowerCase().replace(/[\s-]+/g, '_');

const getMappingStateLabel = (state, entityType) => {
  const entity = entityType === 'contact' ? 'contact' : 'company';
  const labels = {
    mapped: `${entity === 'company' ? 'Company' : 'Contact'} mapping ready`,
    manual_link_required: `Manual ${entity} link needed`,
    missing_in_autotask_quote: `No ${entity} on Autotask quote`,
    mapping_conflict: `Multiple ${entity} mappings`,
    mapping_ambiguous: `${entity === 'company' ? 'Company' : 'Contact'} mapping needs review`,
  };
  return labels[normaliseState(state)] || displayValue(state, `${entity} mapping unknown`).replace(/_/g, ' ');
};

const getQuoteFlags = (quote) => {
  const companyUnknown = isAssociationUnknown(quote.company_association_state);
  const contactUnknown = isAssociationUnknown(quote.contact_association_state);
  const missingCompany = !companyUnknown && isAssociationMissing(
    quote.company_association_state,
    quote.hubspot_company_id,
    quote.hubspot_company_ids,
  );
  const missingContact = !contactUnknown && isAssociationMissing(
    quote.contact_association_state,
    quote.hubspot_contact_id,
    quote.hubspot_contact_ids,
  );
  const companyMismatch = isAssociationMismatch(quote.company_association_state);
  const contactMismatch = isAssociationMismatch(quote.contact_association_state);
  const companyUnverified = isAssociationUnverified(quote.company_association_state);
  const contactUnverified = isAssociationUnverified(quote.contact_association_state);
  const companyMappingState = normaliseState(quote.company_mapping_state);
  const companyMappingConflict = ['mapping_conflict', 'mapping_ambiguous'].includes(companyMappingState);
  return {
    missingCompany,
    missingContact,
    missingBoth: missingCompany && missingContact,
    companyRepair: (missingCompany || companyMismatch) &&
      canRepairMappedAssociation(quote.company_mapping_state),
    companyMismatch,
    contactMismatch,
    companyUnverified,
    contactUnverified,
    companyUnknown,
    contactUnknown,
    manualCompanyLink: companyMappingState === 'manual_link_required',
    missingSourceCompany: companyMappingState === 'missing_in_autotask_quote',
    companyMappingGap: companyMappingState === 'manual_link_required' ||
      companyMappingState === 'missing_in_autotask_quote' || companyMappingConflict,
  };
};

const formatNumber = (value) => new Intl.NumberFormat('en-AU').format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return 'Not supplied';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const displayValue = (value, fallback = '—') => hasValue(value) ? String(value) : fallback;

const getWarningText = (warning) => {
  if (typeof warning === 'string') return warning;
  return warning?.message || warning?.warning || JSON.stringify(warning);
};

const getCacheLabel = (value) => {
  if (typeof value === 'string') return value;
  return value?.status || value?.state || (value ? JSON.stringify(value) : 'not reported');
};

const getHubSpotQuoteUrl = (quoteId) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/quote/${quoteId}`;

const getAutotaskQuoteUrl = (quoteId) =>
  `https://ww29.autotask.net/Mvc/CRM/QuoteDetail.mvc?ids%5B0%5D=${quoteId}&QuoteId=${quoteId}`;

const getHubSpotCompanyUrl = (companyId) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/company/${companyId}`;

const getHubSpotContactUrl = (contactId) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/contact/${contactId}`;

const initialCompanyFor = (company) => ({
  ...(company.company_details || {}),
  id: company.company_details?.id || company.autotask_company_id,
  companyName: company.company_details?.companyName ||
    company.company_details?.company_name || company.company_name,
});

const SummaryCard = ({ label, value, tone = 'neutral', detail }) => (
  <div className={`quote-gap-summary-card ${tone}`}>
    <span className="quote-gap-summary-label">{label}</span>
    <strong className="quote-gap-summary-value">{formatNumber(value)}</strong>
    {detail && <span className="quote-gap-summary-detail">{detail}</span>}
  </div>
);

const AssociationBadge = ({ missing, label }) => (
  <span className={`quote-gap-badge ${missing ? 'missing' : 'present'}`}>
    {missing ? '● Missing' : '✓ Linked'} {label}
  </span>
);

const QuoteLinks = ({ quote }) => (
  <div className="quote-gap-links">
    {hasValue(quote.autotask_quote_id) && (
      <a href={getAutotaskQuoteUrl(quote.autotask_quote_id)} target="_blank" rel="noreferrer">
        AT {quote.autotask_quote_id}
      </a>
    )}
    {hasValue(quote.hubspot_quote_id) && (
      <a href={getHubSpotQuoteUrl(quote.hubspot_quote_id)} target="_blank" rel="noreferrer">
        HS {quote.hubspot_quote_id}
      </a>
    )}
  </div>
);

const ActualAssociationLinks = ({ ids, type }) => {
  const actualIds = Array.isArray(ids) ? ids.filter(hasValue) : [];
  if (!actualIds.length) return null;
  const urlFor = type === 'company' ? getHubSpotCompanyUrl : getHubSpotContactUrl;
  return (
    <span className="quote-gap-actual-links">
      Actual:{' '}
      {actualIds.map((id, index) => (
        <React.Fragment key={id}>
          {index > 0 && ', '}
          <a href={urlFor(id)} target="_blank" rel="noreferrer">HS {id}</a>
        </React.Fragment>
      ))}
    </span>
  );
};

const CompaniesToLink = ({ companies, onReview }) => {
  if (!companies.length) {
    return (
      <div className="quote-gap-empty" role="status">
        <span className="quote-gap-empty-icon" aria-hidden="true">✓</span>
        <h3>No company mappings need manual review</h3>
        <p>Nothing is waiting for a manual company link. Other association repairs may still appear under All quote gaps.</p>
      </div>
    );
  }

  return (
    <div className="quote-company-list">
      {companies.map((company) => {
        const details = company.company_details || {};
        const companyId = company.autotask_company_id;
        const location = [details.city, details.state].filter(Boolean).join(', ');
        return (
          <article className="quote-company-card" key={companyId || company.company_name}>
            <div className="quote-company-heading">
              <div>
                <div className="quote-company-eyebrow">Autotask company {displayValue(companyId)}</div>
                <h3>{displayValue(company.company_name, `Company ${displayValue(companyId)}`)}</h3>
                {(details.webAddress || details.web_address || details.phone || location) && (
                  <p>
                    {[details.webAddress || details.web_address, details.phone, location]
                      .filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <div className="quote-company-review">
                <div className="quote-company-counts">
                  <strong>{formatNumber(company.quote_count)}</strong> quote{Number(company.quote_count) === 1 ? '' : 's'}
                  {Number(company.missing_contact_count) > 0 && (
                    <span>{formatNumber(company.missing_contact_count)} also missing contact</span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => onReview(company)}
                  disabled={!hasValue(companyId)}
                  aria-label={`Review and link ${company.company_name || `Autotask company ${companyId}`}`}
                >
                  Review &amp; link
                </button>
              </div>
            </div>

            <div className="quote-company-quotes" aria-label={`Quotes for ${company.company_name || companyId}`}>
              {(company.quotes || []).map((quote) => (
                <div className="quote-company-quote" key={`${quote.autotask_quote_id}-${quote.hubspot_quote_id}`}>
                  <span>{displayValue(quote.quote_name, `Quote ${displayValue(quote.autotask_quote_id)}`)}</span>
                  <QuoteLinks quote={quote} />
                </div>
              ))}
              {(!company.quotes || company.quotes.length === 0) && (
                <div className="quote-company-quote muted">No quote details were returned.</div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

const AllQuoteGaps = ({ quotes }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  const filteredQuotes = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return quotes.filter((quote) => {
      const flags = getQuoteFlags(quote);
      const matchesFilter = filter === 'all' ||
        (filter === 'company' && flags.missingCompany) ||
        (filter === 'company_mapping_gap' && flags.companyMappingGap) ||
        (filter === 'manual_company_link' && flags.manualCompanyLink) ||
        (filter === 'missing_source_company' && flags.missingSourceCompany) ||
        (filter === 'contact' && flags.missingContact) ||
        (filter === 'both' && flags.missingBoth) ||
        (filter === 'company_repair' && flags.companyRepair) ||
        (filter === 'company_mismatch' && flags.companyMismatch);
      if (!matchesFilter) return false;
      if (!searchTerm) return true;

      return [
        quote.quote_name,
        quote.autotask_quote_id,
        quote.hubspot_quote_id,
        quote.company_name,
        quote.autotask_company_id,
        quote.hubspot_company_id,
        (quote.hubspot_company_ids || []).join(' '),
        (quote.mapped_hubspot_company_ids || []).join(' '),
        quote.contact_name,
        quote.autotask_contact_id,
        quote.hubspot_contact_id,
        (quote.hubspot_contact_ids || []).join(' '),
        (quote.mapped_hubspot_contact_ids || []).join(' '),
        quote.opportunity_id,
        quote.hubspot_deal_id,
      ].some((value) => String(value || '').toLowerCase().includes(searchTerm));
    });
  }, [filter, quotes, search]);

  const totalPages = Math.max(1, Math.ceil(filteredQuotes.length / QUOTES_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pagedQuotes = filteredQuotes.slice(
    (safePage - 1) * QUOTES_PER_PAGE,
    safePage * QUOTES_PER_PAGE,
  );

  const changeSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const changeFilter = (event) => {
    setFilter(event.target.value);
    setPage(1);
  };

  return (
    <div>
      <div className="quote-gap-filters">
        <div className="quote-gap-filter-field wide">
          <label htmlFor="quote-gap-search">Search quote gaps</label>
          <input
            id="quote-gap-search"
            className="search-input"
            type="search"
            value={search}
            onChange={changeSearch}
            placeholder="Quote, company, contact, opportunity or ID"
          />
        </div>
        <div className="quote-gap-filter-field">
          <label htmlFor="quote-gap-filter">Show</label>
          <select id="quote-gap-filter" className="filter-select" value={filter} onChange={changeFilter}>
            <option value="all">All quote gaps</option>
            <option value="company">Missing HubSpot company association</option>
            <option value="company_mapping_gap">No usable source company mapping</option>
            <option value="manual_company_link">Needs manual company link</option>
            <option value="missing_source_company">No company on Autotask quote</option>
            <option value="contact">Missing contact</option>
            <option value="both">Missing both</option>
            <option value="company_mismatch">Associated to a different company</option>
            <option value="company_repair">Company mapped — HubSpot quote needs repair</option>
          </select>
        </div>
      </div>

      <div className="quote-gap-result-count" aria-live="polite">
        {formatNumber(filteredQuotes.length)} matching quote{filteredQuotes.length === 1 ? '' : 's'}
      </div>

      {pagedQuotes.length === 0 ? (
        <div className="quote-gap-empty compact" role="status">
          <h3>No quote gaps match these filters</h3>
          <p>Clear the search or choose a different association type.</p>
        </div>
      ) : (
        <div className="table-container quote-gap-table-container">
          <table className="data-table quote-gap-table">
            <thead>
              <tr>
                <th>Quote</th>
                <th>Company</th>
                <th>Contact</th>
                <th>Association gap</th>
                <th>Last synced</th>
              </tr>
            </thead>
            <tbody>
              {pagedQuotes.map((quote) => {
                const flags = getQuoteFlags(quote);
                return (
                  <tr key={`${quote.autotask_quote_id}-${quote.hubspot_quote_id}`}>
                    <td>
                      <strong className="quote-gap-primary-text">
                        {displayValue(quote.quote_name, `Quote ${displayValue(quote.autotask_quote_id)}`)}
                      </strong>
                      <QuoteLinks quote={quote} />
                    </td>
                    <td>
                      <strong className="quote-gap-primary-text">{displayValue(quote.company_name, 'Unknown company')}</strong>
                      <span className="quote-gap-secondary-text">AT {displayValue(quote.autotask_company_id)}</span>
                      {hasValue(quote.hubspot_company_id) && (
                        <a className="quote-gap-record-link" href={getHubSpotCompanyUrl(quote.hubspot_company_id)} target="_blank" rel="noreferrer">
                          Expected HS {quote.hubspot_company_id}
                        </a>
                      )}
                      {flags.companyMismatch && (
                        <ActualAssociationLinks ids={quote.hubspot_company_ids} type="company" />
                      )}
                      <span className={`quote-gap-mapping-state ${isMapped(quote.company_mapping_state) ? 'mapped' : 'unmapped'}`}>
                        {getMappingStateLabel(quote.company_mapping_state, 'company')}
                      </span>
                    </td>
                    <td>
                      <strong className="quote-gap-primary-text">{displayValue(quote.contact_name, 'No contact')}</strong>
                      <span className="quote-gap-secondary-text">AT {displayValue(quote.autotask_contact_id)}</span>
                      {hasValue(quote.hubspot_contact_id) && (
                        <a className="quote-gap-record-link" href={getHubSpotContactUrl(quote.hubspot_contact_id)} target="_blank" rel="noreferrer">
                          Expected HS {quote.hubspot_contact_id}
                        </a>
                      )}
                      {flags.contactMismatch && (
                        <ActualAssociationLinks ids={quote.hubspot_contact_ids} type="contact" />
                      )}
                      <span className="quote-gap-mapping-state">
                        {getMappingStateLabel(quote.contact_mapping_state, 'contact')}
                      </span>
                    </td>
                    <td>
                      <div className="quote-gap-badges">
                        {flags.companyUnknown ? (
                          <span className="quote-gap-badge repair">Company audit unavailable</span>
                        ) : flags.companyMismatch ? (
                          <span className="quote-gap-badge mismatch">Different company associated</span>
                        ) : flags.companyUnverified ? (
                          <span className="quote-gap-badge repair">Company association unverified</span>
                        ) : (
                          <AssociationBadge missing={flags.missingCompany} label="company" />
                        )}
                        {flags.contactUnknown ? (
                          <span className="quote-gap-badge repair">Contact audit unavailable</span>
                        ) : flags.contactMismatch ? (
                          <span className="quote-gap-badge mismatch">Different contact associated</span>
                        ) : flags.contactUnverified ? (
                          <span className="quote-gap-badge repair">Contact association unverified</span>
                        ) : (
                          <AssociationBadge missing={flags.missingContact} label="contact" />
                        )}
                        {flags.companyRepair && (
                          <span className="quote-gap-badge repair">Needs quote repair</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="quote-gap-secondary-text">{formatDateTime(quote.last_synced_at)}</span>
                      {(hasValue(quote.opportunity_id) || hasValue(quote.hubspot_deal_id)) && (
                        <span className="quote-gap-secondary-text">
                          Opp {displayValue(quote.opportunity_id)} · Deal {displayValue(quote.hubspot_deal_id)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filteredQuotes.length > QUOTES_PER_PAGE && (
        <nav className="quote-gap-pagination" aria-label="Quote gap pages">
          <span>
            Showing {(safePage - 1) * QUOTES_PER_PAGE + 1}–{Math.min(safePage * QUOTES_PER_PAGE, filteredQuotes.length)} of {filteredQuotes.length}
          </span>
          <div>
            <button type="button" className="page-btn" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>
              Previous
            </button>
            <span aria-live="polite">Page {safePage} of {totalPages}</span>
            <button type="button" className="page-btn" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>
              Next
            </button>
          </div>
        </nav>
      )}
    </div>
  );
};

const QuoteAssociationGaps = ({ apiFetch, environment = 'production' }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('companies');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [resolutionNotice, setResolutionNotice] = useState('');

  const loadAudit = useCallback(async () => {
    if (environment !== 'production') {
      setLoading(false);
      setError('This audit is available for production data only.');
      return false;
    }
    setLoading(true);
    setError('');
    try {
      const result = await getAudit(apiFetch, environment);
      setAudit(result);
      return true;
    } catch (requestError) {
      setError(requestError.message || 'Could not load the quote association audit.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiFetch, environment]);

  useEffect(() => {
    let active = true;
    if (environment !== 'production') {
      setAudit(null);
      setLoading(false);
      setError('This audit is available for production data only.');
      return () => { active = false; };
    }
    setLoading(true);
    setError('');
    getAudit(apiFetch, environment)
      .then((result) => {
        if (active) setAudit(result);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'Could not load the quote association audit.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [apiFetch, environment]);

  if (environment !== 'production') {
    return (
      <div className="quote-association-page page-content">
        <div className="quote-gap-state-card" role="alert">
          <h2>Production data only</h2>
          <p>Return to this page from the sidebar to enter the locked production audit.</p>
        </div>
      </div>
    );
  }

  if (loading && !audit) {
    return (
      <div className="quote-association-page page-content">
        <div className="quote-gap-state-card" role="status" aria-live="polite">
          <div className="loading">Loading the cached quote association audit…</div>
          <p>No polling or automatic retries are used.</p>
        </div>
      </div>
    );
  }

  if (error && !audit) {
    return (
      <div className="quote-association-page page-content">
        <div className="quote-gap-state-card error" role="alert">
          <h2>Quote association audit could not be loaded</h2>
          <p>{error}</p>
          <p>The page did not retry automatically.</p>
          <button type="button" className="btn btn-secondary" onClick={loadAudit} disabled={loading}>
            {loading ? 'Trying once…' : 'Try once again'}
          </button>
        </div>
      </div>
    );
  }

  const summary = audit?.summary || {};
  const companies = audit?.companies || [];
  const quotes = audit?.quotes || [];
  const apiUsage = audit?.api_usage || {};
  const warnings = audit?.warnings || [];
  const notices = audit?.notices || [];
  const manualCompanyLinkCount = Number(summary.quotes_requiring_manual_company_link) || 0;
  const missingSourceCompanyCount = Number(summary.quotes_missing_source_company) || 0;
  const companyMappingConflictCount = Number(summary.company_mapping_conflicts) || 0;
  const missingSourceWithHubSpotAssociationCount = quotes.filter((quote) =>
    normaliseState(quote.company_mapping_state) === 'missing_in_autotask_quote' &&
    Array.isArray(quote.hubspot_company_ids) && quote.hubspot_company_ids.some(hasValue)
  ).length;
  const quotesWithoutUsableCompanyMapping = manualCompanyLinkCount +
    missingSourceCompanyCount + companyMappingConflictCount;
  const mappingSummaryParts = [
    `${formatNumber(manualCompanyLinkCount)} manual-link quotes`,
    `${formatNumber(missingSourceCompanyCount)} with no company on the Autotask quote (${formatNumber(missingSourceWithHubSpotAssociationCount)} already associated in HubSpot)`,
  ];
  if (companyMappingConflictCount > 0) {
    mappingSummaryParts.push(`${formatNumber(companyMappingConflictCount)} have conflicting mappings`);
  }

  const handleTabKeyDown = (event) => {
    let nextTab = null;
    if (event.key === 'ArrowRight' || event.key === 'End') nextTab = 'all';
    if (event.key === 'ArrowLeft' || event.key === 'Home') nextTab = 'companies';
    if (!nextTab) return;
    event.preventDefault();
    setActiveTab(nextTab);
    window.requestAnimationFrame(() => {
      document.getElementById(
        nextTab === 'companies' ? 'quote-gap-companies-tab' : 'quote-gap-all-tab'
      )?.focus();
    });
  };

  return (
    <div className="quote-association-page page-content">
      <section className="quote-gap-intro" aria-labelledby="quote-gap-intro-title">
        <div>
          <span className="quote-gap-kicker">Autotask → HubSpot quote audit</span>
          <h2 id="quote-gap-intro-title">Manual links needed for complete quote associations</h2>
          <p>
            A source mapping identifies which company the quote should use; a HubSpot association shows what the quote is linked to now. These are separate checks, so their counts can overlap.
          </p>
        </div>
        <div className="quote-gap-audit-time">
          <span>Last audited</span>
          <strong>{formatDateTime(audit?.audited_at)}</strong>
          <small>Cache expires {formatDateTime(audit?.cache_expires_at)}</small>
        </div>
      </section>

      <div className="quote-gap-history-warning" role="note">
        <span aria-hidden="true">⚠</span>
        <div>
          <strong>Important before linking</strong>
          {notices.length > 0 ? (
            <ul>
              {notices.map((notice, index) => (
                <li key={`${notice}-${index}`}>{notice}</li>
              ))}
            </ul>
          ) : (
            <p>
              Company mapping only fixes future sync routing. Historical quote and contact associations still need a deliberate repair.
            </p>
          )}
        </div>
      </div>

      {resolutionNotice && (
        <div className="quote-gap-resolution-note" role="status">
          <strong>Mapping action completed.</strong> {resolutionNotice}
          <button type="button" onClick={() => setResolutionNotice('')} aria-label="Dismiss mapping notice">×</button>
        </div>
      )}

      {warnings.length > 0 && (
        <section className="quote-gap-api-warnings" aria-label="Audit warnings">
          {warnings.map((warning, index) => (
            <div className="alert warning" key={`${getWarningText(warning)}-${index}`}>
              <span className="alert-icon" aria-hidden="true">⚠</span>
              <div className="alert-content">
                <div className="alert-title">Audit warning</div>
                <div className="alert-message">{getWarningText(warning)}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="quote-gap-summary" aria-label="Quote association summary">
        <SummaryCard label="Synced quotes audited" value={summary.total_synced_quotes} detail={`${formatNumber(summary.correct_company_association)} correct company associations`} />
        <SummaryCard label="Quotes without a usable source company mapping" value={quotesWithoutUsableCompanyMapping} tone="danger" detail={mappingSummaryParts.join(' · ')} />
        <SummaryCard label="Missing HubSpot company association" value={summary.missing_company_association} tone="warning" detail={`${formatNumber(summary.mapped_companies_missing_quote_association)} already have a usable company mapping`} />
        <SummaryCard label="Different company associated" value={summary.mismatched_company_association} tone="danger" detail={`${formatNumber(summary.unverified_company_association)} associations cannot be verified until their source mapping is resolved`} />
        <SummaryCard label="Missing contact" value={summary.missing_contact_association} tone="warning" detail={`${formatNumber(summary.correct_contact_association)} correct · ${formatNumber(summary.mismatched_contact_association)} mismatched`} />
        <SummaryCard label="Missing both" value={summary.missing_both_associations} tone="danger" />
        <SummaryCard label="Mapped quote repairs" value={summary.mapped_companies_needing_quote_repair} tone="warning" />
      </section>

      <div className="quote-gap-tabs" role="tablist" aria-label="Quote association views">
        <button
          id="quote-gap-companies-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === 'companies'}
          aria-controls="quote-gap-companies-panel"
          tabIndex={activeTab === 'companies' ? 0 : -1}
          className={activeTab === 'companies' ? 'active' : ''}
          onClick={() => setActiveTab('companies')}
          onKeyDown={handleTabKeyDown}
        >
          Companies to link
          <span>{formatNumber(summary.distinct_companies_to_link)}</span>
        </button>
        <button
          id="quote-gap-all-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === 'all'}
          aria-controls="quote-gap-all-panel"
          tabIndex={activeTab === 'all' ? 0 : -1}
          className={activeTab === 'all' ? 'active' : ''}
          onClick={() => setActiveTab('all')}
          onKeyDown={handleTabKeyDown}
        >
          All quote gaps
          <span>{formatNumber(quotes.length)}</span>
        </button>
      </div>

      <section className="quote-gap-panel">
        {activeTab === 'companies' ? (
          <div id="quote-gap-companies-panel" role="tabpanel" aria-labelledby="quote-gap-companies-tab">
            <div className="quote-gap-panel-heading">
              <div>
                <h3>Companies requiring a manual decision</h3>
                <p>
                  {formatNumber(manualCompanyLinkCount)} quotes across {formatNumber(summary.distinct_companies_to_link)} companies can be manually mapped here. The {formatNumber(missingSourceCompanyCount)} quotes with no company on the Autotask quote are listed under All quote gaps because there is no source company to link.
                </p>
              </div>
              <div className="quote-gap-contact-note">
                {formatNumber(summary.distinct_contacts_to_link)} contacts across {formatNumber(summary.quotes_requiring_manual_contact_link)} quotes also need linking
              </div>
            </div>
            <CompaniesToLink companies={companies} onReview={setSelectedCompany} />
          </div>
        ) : (
          <div id="quote-gap-all-panel" role="tabpanel" aria-labelledby="quote-gap-all-tab">
            <div className="quote-gap-panel-heading">
              <div>
                <h3>Every quote with an association gap</h3>
                <p>Use the filters to separate missing mappings from historical quote repairs.</p>
              </div>
            </div>
            <AllQuoteGaps quotes={quotes} />
          </div>
        )}
      </section>

      <footer className="quote-gap-safety-note">
        <span aria-hidden="true">🛡</span>
        <div>
          <strong>Rate-limit safe by design</strong>
          <p>
            This page requests one cached audit when opened and never polls or retries automatically. The current audit used {formatNumber(apiUsage.hubspot_requests_for_audit)} HubSpot batch request(s) and {formatNumber(apiUsage.autotask_requests_for_company_labels)} Autotask batch request(s). This page load made {formatNumber(apiUsage.hubspot_requests_this_request)} HubSpot, {formatNumber(apiUsage.autotask_requests_this_request)} Autotask, and {formatNumber(apiUsage.n8n_requests)} n8n request(s). Cache: {getCacheLabel(apiUsage.cache_status)}.
          </p>
        </div>
      </footer>

      {selectedCompany && (
        <ResolveCompanyModal
          key={selectedCompany.autotask_company_id}
          autotaskId={selectedCompany.autotask_company_id}
          initialCompany={initialCompanyFor(selectedCompany)}
          context={null}
          apiFetch={apiFetch}
          onClose={() => setSelectedCompany(null)}
          onResolved={() => {
            const companyName = selectedCompany.company_name || `Autotask company ${selectedCompany.autotask_company_id}`;
            setSelectedCompany(null);
            setResolutionNotice(`${companyName} was mapped. Refreshing this audit once using the server cache…`);
            loadAudit().then((refreshed) => {
              setResolutionNotice(refreshed
                ? `${companyName} was mapped and the list was refreshed once. Historical quote/contact gaps remain listed for repair.`
                : `${companyName} was mapped, but the one-time list refresh failed. No automatic retry was made.`);
            });
          }}
        />
      )}
    </div>
  );
};

export default QuoteAssociationGaps;
